import { getAxiosClient } from '../http.client';
// Helper function to check if actual object matches expected object (partial matching)
function objectMatches(actual, expected) {
    if (expected === null || expected === undefined) {
        return actual === expected;
    }
    if (typeof expected !== 'object') {
        return actual === expected;
    }
    if (Array.isArray(expected)) {
        if (!Array.isArray(actual) || actual.length !== expected.length) {
            return false;
        }
        return expected.every((expectedItem, index) => objectMatches(actual[index], expectedItem));
    }
    // For objects, check that all expected keys match
    for (const key in expected) {
        if (expected.hasOwnProperty(key)) {
            if (!actual.hasOwnProperty(key)) {
                return false;
            }
            if (!objectMatches(actual[key], expected[key])) {
                return false;
            }
        }
    }
    return true;
}
export class TestUiClient {
    client;
    tokenProvider;
    organizationId;
    constructor() {
        this.client = getAxiosClient('admin/test-ui');
    }
    async getTests() {
        const response = await this.client.get('/tests');
        return response.data;
    }
    async getTest(testId) {
        const response = await this.client.get(`/tests/${testId}`);
        return response.data;
    }
    async createTest(test) {
        const response = await this.client.post('/tests', test);
        return response.data;
    }
    async updateTest(testId, updates) {
        const response = await this.client.put(`/tests/${testId}`, updates);
        return response.data;
    }
    async deleteTest(testId) {
        const response = await this.client.delete(`/tests/${testId}`);
        return response.data;
    }
    // Frontend test execution - runs the test cases from the browser
    async runTest(test) {
        const results = [];
        // Get axios client for making authenticated requests
        const axiosClient = getAxiosClient('');
        for (let i = 0; i < test.testCases.length; i++) {
            const testCase = test.testCases[i];
            const result = {
                caseIndex: i,
                caseName: testCase.name || `Test Case ${i + 1}`,
                status: 'pending',
                request: testCase.request,
                expected: testCase.expected,
                actual: null,
                passed: null,
                error: null,
                duration: 0
            };
            const startTime = Date.now();
            try {
                // Handle file if present
                if (testCase.request.file && testCase.request.file.fileId) {
                    console.log('Processing file for test case:', testCase.request.file);
                    try {
                        // Get file content directly from our endpoint
                        const fileResponse = await this.client.get(`/files/${encodeURIComponent(testCase.request.file.fileId)}`, {
                            responseType: 'blob'
                        });
                        const fileBlob = fileResponse.data;
                        console.log('File downloaded successfully, size:', fileBlob.size);
                        // Create FormData similar to processPDF method
                        const formData = new FormData();
                        formData.append('file', fileBlob, testCase.request.file.fileName);
                        // Add body fields to form data (like formKey)
                        if (testCase.request.body) {
                            Object.keys(testCase.request.body).forEach(key => {
                                const value = testCase.request.body[key];
                                formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
                            });
                        }
                        console.log('FormData prepared with file and body fields');
                        // Make the file upload request
                        const response = await axiosClient.request({
                            method: test.method,
                            url: test.path,
                            data: formData,
                            headers: {
                                ...test.headers,
                                'Content-Type': 'multipart/form-data'
                            }
                        });
                        result.actual = response.data;
                    }
                    catch (fileError) {
                        console.error('Failed to download file:', fileError);
                        throw new Error(`Failed to download file: ${fileError instanceof Error ? fileError.message : 'Unknown error'}`);
                    }
                }
                else if (testCase.request.file) {
                    console.warn('File object exists but missing fileId:', testCase.request.file);
                    throw new Error('File object exists but missing fileId');
                }
                else {
                    // Handle regular JSON request
                    const requestData = testCase.request.body || {};
                    // Make the JSON request
                    const response = await axiosClient.request({
                        method: test.method,
                        url: test.path,
                        data: requestData,
                        headers: {
                            ...test.headers,
                            'Content-Type': 'application/json'
                        }
                    });
                    result.actual = response.data;
                }
                result.duration = Date.now() - startTime;
                // Compare with expected result using partial matching
                result.passed = objectMatches(result.actual, testCase.expected);
                result.status = result.passed ? 'passed' : 'failed';
            }
            catch (error) {
                result.duration = Date.now() - startTime;
                result.status = 'failed';
                result.error = error.response?.data?.message || error.message || 'Unknown error';
                result.actual = error.response?.data || null;
                result.passed = false;
            }
            results.push(result);
        }
        const summary = {
            total: results.length,
            passed: results.filter(r => r.status === 'passed').length,
            failed: results.filter(r => r.status === 'failed').length,
            duration: results.reduce((sum, r) => sum + r.duration, 0)
        };
        const testRunResult = {
            testId: test.testId,
            testName: test.testName,
            summary,
            results
        };
        // Save the test results to the backend
        try {
            await this.saveTestResults(test.testId, testRunResult);
        }
        catch (error) {
            console.warn('Failed to save test results:', error);
            // Don't fail the test run if saving results fails
        }
        return testRunResult;
    }
    async saveTestResults(testId, results) {
        const response = await this.client.post(`/tests/${testId}/results`, results);
        return response.data;
    }
    async getTestResults(testId) {
        try {
            const response = await this.client.get(`/tests/${testId}/results`);
            return response.data;
        }
        catch (error) {
            if (error.response?.status === 404) {
                return null; // No results found
            }
            throw error;
        }
    }
    async uploadFile(file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await this.client.post('/upload-file', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }
    async getFile(fileId) {
        const response = await this.client.get(`/files/${encodeURIComponent(fileId)}`, {
            responseType: 'blob'
        });
        return response.data;
    }
}
